import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import Button from '../../../components/Button';
import styles from './LinkEditor.module.scss';
import type { Blot } from '../../types';
import { checkmarkIcon } from '../editorIcons';

interface LinkEditorProps {
  blot: Blot;
  initialLink: string;
  initialText: string;
  textPlaceholder?: string;
  linkPlaceholder?: string;
  editingTarget: 'link' | 'text';
  onEditingTargetChange: (target: 'link' | 'text' | null) => void;
  onSave: (value: string) => void;
}

const unsavedLink = new WeakMap<Blot, string>();
const unsavedText = new WeakMap<Blot, string>();

const LinkEditor = ({
  blot,
  initialLink,
  initialText,
  textPlaceholder,
  linkPlaceholder,
  editingTarget,
  onEditingTargetChange,
  onSave,
}: LinkEditorProps) => {
  const [link, setLink] = useState(unsavedLink.get(blot) || initialLink);
  const [text, setText] = useState(unsavedText.get(blot) || initialText);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSave = () => {
    if (editingTarget === 'text') {
      onSave(text);
      unsavedText.delete(blot);
    } else {
      onSave(link);
      unsavedLink.delete(blot);
    }
    onEditingTargetChange(null);
  };

  return (
    <div className={styles.editor}>
      <div className={styles.tab}>
        <Button
          variant="muted"
          className={classNames(styles.tabItem, {
            [styles.active]: editingTarget === 'link',
          })}
          onClick={() => {
            onEditingTargetChange('link');
            inputRef.current?.focus();
          }}
          aria-label="Edit link"
        >
          Link
        </Button>
        <Button
          variant="muted"
          className={classNames(styles.tabItem, {
            [styles.active]: editingTarget === 'text',
          })}
          onClick={() => {
            onEditingTargetChange('text');
            inputRef.current?.focus();
          }}
          aria-label="Edit text"
        >
          Text
        </Button>
      </div>
      <div className={styles.inputContainer}>
        <input
          ref={inputRef}
          autoFocus
          // Don't use <form> to allow nested inside a form (ex TopicEditModal)
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleSave();
              event.preventDefault();
            } else if (event.key === 'Escape') {
              onEditingTargetChange(null);
            }
          }}
          value={editingTarget === 'text' ? text : link}
          placeholder={
            editingTarget === 'text' ? textPlaceholder : linkPlaceholder
          }
          onChange={(e) => {
            const { value } = e.target;
            if (editingTarget === 'text') {
              unsavedText.set(blot, value);
              setText(value);
            } else {
              unsavedLink.set(blot, value);
              setLink(e.target.value);
            }
          }}
          aria-label={editingTarget === 'text' ? 'Text' : 'Link'}
        />
        <Button
          className={styles.saveButton}
          variant="link"
          disabled={
            editingTarget === 'link'
              ? !link || link === initialLink
              : text === initialText
          }
          icon={checkmarkIcon}
          onClick={() => handleSave()}
          testId="link-editor-save-button"
          aria-label="Save"
        />
      </div>
    </div>
  );
};

export default LinkEditor;
